import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Dialog,
  IconButton,
  Link,
  OutlinedInput
} from '@mui/material'
import { ErrorText, MButton, SvgImage, Text } from 'components'
import { MESSAGES, ROUTES_PATH } from 'config'
import { color } from 'config/color'
import { FormLabel } from 'containers/Auth/SignIn/component'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { APIs, type IAccountLookup } from 'services'
import * as yup from 'yup'

const schema = yup
  .object({
    legacyAccountId: yup.string().required('Legacy Account number is required'),
    lastNameBusinessName: yup.string().required('Last name/Business name is required')
  })
  .required()


export const Form: React.FC = () => {
  const [account, setAccount] = useState<string | null>(null)
  const [open, setOpen] = useState<boolean>(false)
  const {
    handleSubmit,
    control,
    formState: { errors, isValid }
  } = useForm<IAccountLookup>({
    mode: 'all',
    defaultValues: {
      legacyAccountId: '',
      lastNameBusinessName: ''
    },
    resolver: yupResolver(schema)
  })
  const { enqueueSnackbar } = useSnackbar()

  const onSubmit = async (body: IAccountLookup): Promise<void> => {
    try {
      const response = await APIs.accountLookup(body)
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      if (response.data?.ccsAccountId) {
        setAccount(response.data.ccsAccountId)
        setOpen(true)
      } else {
        enqueueSnackbar(MESSAGES.VERIFY_PAY_BILL_ERROR, { variant: 'error' })
      }
    } catch (error: any) {
      const { data } = error.response
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      enqueueSnackbar(data.error || MESSAGES.VERIFY_PAY_BILL_ERROR, { variant: 'error' })
    }
  }

  const AccountLookupModal = (): JSX.Element => {
    return (
      <Dialog
        open={open}
        onClose={() => { setOpen(false) }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: '40px 32px',
            textAlign: 'center'
          }
        }}
      >
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <IconButton
            onClick={() => { setOpen(false) }}
            sx={{
              padding: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            }}
          >
            <Text fontSize="18px" color={color.background[600]}>×</Text>
          </IconButton>
        </Box>
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
          <SvgImage name="GreenCheckIcon" width={250} height={62} />
        </Box>
        <Box>
          <Text
            heading="h2"
            color={color.grey[900]}
            fontSize="24px"
            align="center"
            margin="16px 0 30px"
            fontWeight="600"
          >
            Account Lookup Successful!
          </Text>
        </Box>

        <Box sx={{ mb: 4, textAlign: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ mb: 2 }}>
            <Text fontSize="16px" color={color.background[600]}>
              Your new account number is:
            </Text>
          </Box>
          <Box sx={{
            backgroundColor: color.background[50],
            padding: '16px 24px',
            borderRadius: '12px',
            border: `2px solid ${color.primary[200]}`,
            display: 'inline-block',
            mb: 3
          }}>
            <Text fontSize="32px" fontWeight="700" color={color.primary[700]}>
              {account}
            </Text>
          </Box>
          <Box>
            <Text fontSize="14px" color={color.background[500]}>
              Use this account number to <Link href={ROUTES_PATH.SIGNIN} underline="none">register</Link> or <Link href={ROUTES_PATH.SIGNIN} underline="none">pay your bill</Link>.
            </Text>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <MButton
            variant="outlined"
            size="large"
            rounded="true"
            texttransform="none"
            onClick={() => { window.location.href = ROUTES_PATH.SIGNIN }}
            sx={{
              minWidth: '140px',
              borderColor: color.primary[600],
              color: color.primary[600],
              '&:hover': {
                borderColor: color.primary[700],
                color: color.primary[700],
                backgroundColor: 'transparent'
              }
            }}
          >
            Back to Login
          </MButton>
        </Box>
      </Dialog>
    )
  }

  return (
    <>
      <Box>
        <form method="post" onSubmit={handleSubmit(onSubmit)}>
          <AccountLookupModal />
          <Box mb={2}>
            <FormLabel>Legacy Account Number</FormLabel>
            <Controller
              name="legacyAccountId"
              control={control}
              render={({ field: { onChange, value, name } }): JSX.Element => (
                <OutlinedInput
                  name={name}
                  placeholder="Enter your legacy account number"
                  onChange={onChange}
                  value={value}
                  type="text"
                  fullWidth
                />
              )}
            />
            {(errors.legacyAccountId != null) && <ErrorText>{errors.legacyAccountId.message}</ErrorText>}
          </Box>
          <Box mb={2}>
            <FormLabel>Last Name/Business Name</FormLabel>
            <Controller
              name="lastNameBusinessName"
              control={control}
              render={({ field: { onChange, value, name } }): JSX.Element => (
                <OutlinedInput
                  name={name}
                  placeholder="Enter your last name"
                  onChange={onChange}
                  value={value}
                  type="text"
                  fullWidth
                />
              )}
            />
            {(errors.lastNameBusinessName != null) && <ErrorText>{errors.lastNameBusinessName.message}</ErrorText>}
          </Box>

          <Box mt={3} mb={2} width="100%">
            <MButton
              variant="contained"
              type="submit"
              size="large"
              rounded="true"
              disabled={!isValid}
              texttransform="none"
              fullWidth
              onSubmit={handleSubmit(onSubmit)}
            >
              Lookup Account
            </MButton>

            <Box mt={3}>
              <Link href={ROUTES_PATH.SIGNIN} underline="none">
                <Text color={color.background[900]} fontSize="16px" align="center">
                  Back to Login
                </Text>
              </Link>
            </Box>
          </Box>
        </form >
      </Box >
    </>
  )
}
